import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'lodash-es';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-upload-image',
  templateUrl: './input-upload-image.component.html',
  styleUrls: ['./input-upload-image.component.scss', './../material-image/material-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputUploadImageComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => InputUploadImageComponent),
    },
  ],
})
export class InputUploadImageComponent extends IInputComponent<File | string> implements OnInit, OnDestroy, Validator {
  @Input()
  public supportedFiles: string[];
  @Input()
  public maximumFileSizeMB: number = 2;
  @ViewChild('preview')
  public previewRef: ElementRef<HTMLImageElement>;

  private fileSubscription$: Subscription = Subscription.EMPTY;
  private fileInput: HTMLInputElement;

  public imageSource: string;
  public textControl: UntypedFormControl = new UntypedFormControl();
  public previewNotSupported$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public previewDimension$: BehaviorSubject<string> = new BehaviorSubject(null);

  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly injector: Injector,
    private readonly renderer: Renderer2,
    private readonly translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.fileInput = <HTMLInputElement>this.renderer.createElement('input');
    this.fileInput.setAttribute('type', 'file');
    this.fileInput.setAttribute('accept', this.supportedFiles.join(','));

    this.fileSubscription$ = fromEvent(this.fileInput, 'change')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const fileInput = this.fileInput;
        this.textControl.setValue(null);

        if (this.ngControl) {
          this.ngControl.control.reset();
        }

        this.previewNotSupported$.next(false);
        const file: File = first(fileInput.files);

        if (this.ngControl) {
          this.ngControl.control.markAsTouched();
          this.ngControl.control.markAsDirty();
          this.ngControl.control.setValue(file, { emitEvent: true });
        }
        this.readFile(file);
        this.textControl.setValue(file.name);
      });
  }

  public writeValue(value: File | string): void {
    super.writeValue(value);

    if (value && typeof value === 'string') {
      this.imageSource = value;
      this.textControl.setValue('image.png');
    } else if (value) {
      const file: File = value as File;
      this.textControl.setValue(file.name);
      this.readFile(file);
    }
  }

  public validate<T>(control: AbstractControl): { [key: string]: T } {
    let result = null;

    if (control && control.value) {
      if (typeof control.value === 'string') {
        return null;
      }

      if (!this.supportedFiles.some((type) => control.value.type === type)) {
        result = { invalidFileType: this.supportedFiles };
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      } else if (control.value.size / 1024 / 1024 > this.maximumFileSizeMB) {
        result = { invalidFileSize: this.maximumFileSizeMB };
      }
    }
    return result;
  }

  public readFile(file: File): void {
    const fileReader = new FileReader();
    fileReader.onload = (event: any): void => {
      this.imageSource = event.target.result;
      this.changeDetectionRef.markForCheck();
    };
    fileReader.readAsDataURL(file);
  }

  public openFileDialog(): void {
    this.fileInput.click();
  }

  public remove(): void {
    this.value = null;
    this.imageSource = null;
    this.textControl.setValue(null);
    if (this.ngControl) {
      this.ngControl.control.markAsDirty();
      this.ngControl.control.setValue(null, { emitEvent: true });
    }
    this.changeDetectionRef.markForCheck();
  }

  public onLoaded(): void {
    this.previewDimension$.next(`${this.previewRef.nativeElement.naturalWidth}x${this.previewRef.nativeElement.naturalHeight}`);
  }

  public ngOnDestroy(): void {
    this.fileSubscription$.unsubscribe();
  }
}
